import React, { useContext, useEffect } from 'react';
import { useLocation, navigate } from '@reach/router';
import * as Ink from '@carta/ink';
import * as Sentry from '@sentry/react';

import '@carta/ink/dist/ink.css';
import '../../styles/index.scss';
import { LayoutContainer, MainContent, SearchContainer, SideNavContainer } from './styles';

import { AnalyticsProvider } from '../../contexts/AnalyticsContext';
import DocsContext from '../../contexts/DocsContext';

import SideNavigation from '../SideNavigation/SideNavigation';
import { MobileNavigation } from '../SideNavigation/MobileNavigation';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import SiteSearch from '../SiteNavigation/SiteSearch';
import VersionBanner from '../Common/VersionBanner';
import PreferencesModal from '../PreferencesModal/PreferencesModal';
import FeedbackButton from '../FeedbackButton/FeedbackButton';
import Logo from '../Common/Logo';

const ErrorFallback = () => (
  <Ink.EmptyState type="page" text="Something went wrong" icon="error">
    <Ink.Anchor type="primary" href="https://grid-carta.enterprise.slack.com/archives/CBFKQSKTN">
      Click here to let us know!
    </Ink.Anchor>
  </Ink.EmptyState>
);

const Layout: React.FC<{ sidebarOnly?: boolean }> = ({ children, sidebarOnly = false }) => {
  const { updateAvailable, isPreferencesModalOpen, setIsPreferencesModalOpen, isDarkPage } = useContext(DocsContext);
  const location = useLocation();
  const isMobileLayout = useIsMobileLayout();

  // Scroll to top on pathname change (but not on hash changes)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCloseModal = () => {
    setIsPreferencesModalOpen(false);

    // If we're on the preferences page, navigate away
    if (location.pathname.includes('/preferences')) {
      if (typeof window !== 'undefined' && window.history.length > 2) {
        // Check if the referrer is from ink.carta.com to avoid navigating outside the site
        const referrer = document.referrer;
        let isFromInkSite = false;
        if (referrer) {
          try {
            const referrerUrl = new URL(referrer);
            isFromInkSite = referrer.includes('ink.carta.com') || referrerUrl.hostname === window.location.hostname;
          } catch {
            // Invalid referrer URL, treat as external
            isFromInkSite = false;
          }
        }

        if (isFromInkSite) {
          // Safe to go back - referrer is from our site
          window.history.back();
        } else {
          // Referrer is from outside or unknown, go to overview
          navigate('/overview');
        }
      } else {
        // No history, go to overview
        navigate('/overview');
      }
    }
  };

  return (
    <AnalyticsProvider>
      <div>
        <Ink.ContextProvider>
          <LayoutContainer>
            <SideNavContainer className="sidenav-scrollbar">
              <SideNavigation />
            </SideNavContainer>
            <MainContent>
              {sidebarOnly ? (
                children
              ) : (
                <Ink.Grid>
                  <SearchContainer $showMobileNav={isMobileLayout}>
                    {updateAvailable && (
                      <VersionBanner $variant="dark" style={{ marginBottom: '18px', marginTop: '0' }} />
                    )}
                    {isMobileLayout ? (
                      <Ink.HStack spacing="medium" align="distributed" alignY="center">
                        <Ink.HStack spacing="medium" alignY="center">
                          <MobileNavigation />
                          <Logo />
                        </Ink.HStack>
                        <SiteSearch />
                      </Ink.HStack>
                    ) : (
                      <SiteSearch />
                    )}
                  </SearchContainer>
                  <Sentry.ErrorBoundary fallback={ErrorFallback}>
                    <Ink.Box all={['none', 'none', 'xlarge']} top={['xlarge', 'xlarge', 'xlarge']}>
                      {children}
                    </Ink.Box>
                  </Sentry.ErrorBoundary>
                </Ink.Grid>
              )}
            </MainContent>
          </LayoutContainer>
          {isPreferencesModalOpen && <PreferencesModal close={handleCloseModal} />}
          {!isDarkPage && (
            <FeedbackButton url="https://docs.google.com/forms/d/e/1FAIpQLSfZWnJmudus2YXUiWNCNZ9RqA5yYMsMhl1MrQtJLFYDRvOoeg/viewform?usp=sharing&ouid=109851538523011000028" />
          )}
        </Ink.ContextProvider>
      </div>
    </AnalyticsProvider>
  );
};

export default Layout;
